import BigNumber from 'bignumber.js';

import { TenantGlobalConfig } from '@/components/Intl/IntlContext/helper';

const CommonParseRate = 100; // 元转分 比例

type SourceValue = BigNumber.Value;

// 是否为空值
const NilSymbolList = [undefined, null, ''];
export const isNil = (val) => NilSymbolList.includes(val);

// 转小数
export const format2Decimal: (target: SourceValue, rate?: number, len?: number) => string = (
  target,
  rate,
  len,
) => {
  if (isNil(target)) return null;
  const num = new BigNumber(target);
  if (num.isNaN()) return null;
  return num.dividedBy(rate || 1).toFixed(len || 2, BigNumber.ROUND_DOWN);
};

// 转整数
export const format2Integer: (target: SourceValue, rate?: number) => string = (target, rate) => {
  if (isNil(target)) return null;
  const num = new BigNumber(target);
  if (num.isNaN()) return null;
  return num.dividedBy(rate || 1).toFixed(0, BigNumber.ROUND_DOWN);
};

// 转数字
export const parse2Number = (val) => {
  if (isNil(val)) return null;
  const num = new BigNumber(val);
  if (num.isNaN()) return null;
  return num.toNumber();
};

// 元转货币
export const parseYuan2Currency = (val, keepSymbol = true) => {
  if (isNil(val)) return null;
  const num = new BigNumber(!keepSymbol ? Math.abs(val) : val);
  if (num.isNaN()) return null;
  return `${TenantGlobalConfig.getCurrencySymbol()} ${num.toFixed(2)}`;
};

// 分转货币
export const parseFen2Currency = (val, keepSymbol = true) => {
  if (isNil(val)) return null;
  const num = new BigNumber(!keepSymbol ? Math.abs(val) : val);
  if (num.isNaN()) return null;
  return `${TenantGlobalConfig.getCurrencySymbol()} ${num.dividedBy(CommonParseRate).toFixed(2)}`;
};

// 元转分
export const parseYuan2Fen = (val) => {
  if (isNil(val)) return null;
  const num = new BigNumber(val);
  if (num.isNaN()) return null;
  return num.multipliedBy(CommonParseRate).toFixed(0);
};

// 分转元
export const parseFen2Yuan = (val) => {
  if (isNil(val)) return null;
  const num = new BigNumber(val);
  if (num.isNaN()) return null;
  return num.dividedBy(CommonParseRate).toFixed(2);
};

/**
 * 0~1 折扣转化 保留一位小数
 * 0.5 -> 5.0
 * 0.65 -> 6.5 折
 */
export const parseValueToDiscount: (target: SourceValue) => string = (target) => {
  if (isNil(target)) return '';
  return format2Decimal(new BigNumber(target).multipliedBy(10), 1);
};

export const parseValueToDiscountLabel = (target: number) => {
  if (isNil(target)) return '';
  return new BigNumber(target).multipliedBy(10).toFixed(1);
};

/**
 * 1%~100% 折扣转化 保留一位小数
 * 50(%) -> 5.0 折
 * 65(%) -> 6.5 折
 */
export const parsePercentValueToDiscount: (target: SourceValue) => string = (target) => {
  if (isNil(target)) return '';
  return format2Decimal(new BigNumber(target).dividedBy(10), 1);
};

/**
 * 0.1~10 折扣转化 保留两位小数  ctrip传给后端转化
 * 5.5 -> 0.55
 * 8.8 -> 0.88
 */
export const dividedBy10And2Decimal: (target: SourceValue) => string = (target) => {
  if (isNil(target)) return '';
  return format2Decimal(new BigNumber(target).dividedBy(10), 1);
};

// 计算器
export class Calculator {
  val: BigNumber;

  constructor(initVal?: SourceValue) {
    this.val = new BigNumber(initVal || 0);
  }

  // 加
  plus = (target: SourceValue) => {
    this.val = this.val.plus(target);
    return this;
  };

  // 减
  minus = (target: SourceValue) => {
    this.val = this.val.minus(target);
    return this;
  };

  // 乘
  multipliedBy = (target: SourceValue) => {
    this.val = this.val.multipliedBy(target);
    return this;
  };

  // 除
  dividedBy = (target: SourceValue) => {
    this.val = this.val.dividedBy(target);
    return this;
  };

  getValue = () => {
    return this.val.valueOf();
  };
}

// 百分比转换
export const parseToPercentText = (val) => {
  return `${new BigNumber(val).absoluteValue().toFixed(2)}%`;
};

// 转换格式 12345.67 -> 12,345.67
const parseValueFormat = (val: string | number, decimal?: number) => {
  const formatRule = {
    // 小数分隔符
    decimalSeparator: '.',
    // 分组分隔符
    groupSeparator: ',',
    // 分组大小
    groupSize: 3,
    // 剩余组分组大小
    secondaryGroupSize: 0,
    // 小数分组分隔符号
    fractionGroupSeparator: '',
    // 小数分组大小
    fractionGroupSize: 0,
  };
  if (typeof decimal === 'number') {
    return new BigNumber(val).toFormat(decimal, formatRule);
  }
  return new BigNumber(val).toFormat(formatRule);
};

// 数值格式化
export const formatValueText = (val: string | number, unit: string) => {
  if (isNil(val)) return '-';
  if (unit === '%') return parseToPercentText(val);
  if (unit === '元') return parseValueFormat(val, 2);
  return parseValueFormat(val);
};

export default BigNumber;
